<script setup lang="ts">
import { getDestinationSlug } from '~/helpers/getDestinationSlug'
import type { HouseDetailsAttributes, HouseSlug } from 'lc-services/types'

const {
  houseDestinations = {} as HouseDetailsAttributes['destination'],
  withoutLinks = false,
  withCountry = false,
} = defineProps<{
  houseDestinations?: HouseDetailsAttributes['destination']
  withoutLinks?: boolean
  withCountry?: boolean
}>()

const { locale } = useI18n()

const houseLocalisations = computed(() => {
  const destination = houseDestinations
  const cluster = houseDestinations?.cluster
  const country = houseDestinations?.country

  const destis = [
    ...(destination?.name && destination.destinationUrl
      ? [
          {
            name: destination.name,
            slug: destination.destinationUrl,
            searchUrl: destination.destinationUrl,
          },
        ]
      : []),

    ...(cluster?.name && cluster.slug
      ? [
          {
            name: cluster.name,
            slug: cluster.slug,
            searchUrl: cluster.searchUrl,
          },
        ]
      : []),

    ...(country?.name && withCountry && country.slug
      ? [
          {
            name: country.name,
            slug: country.slug,
            searchUrl: country.searchUrl,
          },
        ]
      : []),
  ]

  const result = destis.filter((x) => typeof x.name !== 'undefined')

  return withoutLinks ? result : [...result].reverse()
})

const getHref = (slug?: HouseSlug) => {
  if (slug) return getDestinationSlug(slug)[locale.value].path
}
</script>

<template>
  <div
    class="flex flex-wrap items-center text-md"
    :class="{ 'mb-2': withoutLinks }"
  >
    <template
      v-for="(localisation, index) in houseLocalisations"
      :key="localisation.slug[locale]"
    >
      <span v-if="withoutLinks" class="text-gray-600">
        {{ localisation.name }}
      </span>
      <NuxtLink
        v-else
        :to="getHref(localisation.searchUrl ?? localisation.searchUrl)"
        :class="[
          'house-destination-breadcrumb__link',
          {
            'font-bold':
              !withoutLinks && index === houseLocalisations.length - 1,
            '!text-gray-500': !withoutLinks,
            'text-sm': !withoutLinks,
          },
        ]"
      >
        {{ localisation.name }}
      </NuxtLink>

      <template v-if="index !== houseLocalisations.length - 1">
        <BaseIcon
          v-if="!withoutLinks"
          name="chevronRight"
          :size="1.25"
          class="mx-1"
          color="text-gray-500"
        />
        <span v-else class="mr-1">,&nbsp;</span>
      </template>
    </template>
  </div>
</template>

<style scoped>
.house-destination-breadcrumb__link,
.house-destination-breadcrumb__link:hover,
.house-destination-breadcrumb__link:focus {
  @apply text-gray-600;
}
</style>
